import React, { useState, useMemo } from 'react';
import { Plus } from 'lucide-react';

/**
 * Combobox reutilizable de emplazamientos con opción de código manual.
 *
 * Props:
 *  - sites: lista de sites del portfolio (con id, code, name, address, municipality)
 *  - siteId: id seleccionado del portfolio (o vacío)
 *  - manualCode: código manual escrito por el usuario (o vacío)
 *  - onChange({ siteId, manualCode }): callback cuando cambia
 *  - required: bool para asterisco
 *  - testIdPrefix: prefijo para los data-testid ('neg', 'ofr', 'prev'...)
 */
const SitePicker = ({
  sites = [], siteId = '', manualCode = '',
  onChange, required = true, testIdPrefix = 'site',
}) => {
  const [search, setSearch] = useState('');

  const filteredSites = useMemo(() => {
    if (!search) return sites;
    const q = search.toLowerCase();
    return sites.filter((s) =>
      (s.name || '').toLowerCase().includes(q)
      || (s.code || '').toLowerCase().includes(q)
      || (s.address || '').toLowerCase().includes(q)
      || (s.municipality || '').toLowerCase().includes(q));
  }, [sites, search]);

  const selectedSite = sites.find((s) => s.id === siteId);
  const hasSelection = !!(selectedSite || manualCode);

  const clear = () => { onChange({ siteId: '', manualCode: '' }); setSearch(''); };
  const pickSite = (s) => { onChange({ siteId: s.id, manualCode: '' }); setSearch(''); };
  const useManual = () => { onChange({ siteId: '', manualCode: search.trim() }); setSearch(''); };

  return (
    <div>
      <span className="text-xs uppercase text-white/60">Emplazamiento{required ? ' *' : ''}</span>
      {hasSelection ? (
        <div className={`flex items-center justify-between gap-3 rounded-lg px-3 py-2 mt-1 ${manualCode && !selectedSite ? 'bg-orange-500/10 border border-orange-500/40' : 'bg-[#A3FF12]/10 border border-[#A3FF12]/40'}`}
          data-testid={`${testIdPrefix}-selected-site-chip`}>
          <div className="min-w-0">
            <div className="text-white font-bold text-sm truncate">
              {selectedSite ? selectedSite.name : manualCode}
              {selectedSite?.code && <span className="text-white/50 font-normal text-xs"> · {selectedSite.code}</span>}
              {manualCode && !selectedSite && <span className="text-orange-300 font-normal text-[10px] ml-2">CÓDIGO MANUAL</span>}
            </div>
            <div className="text-white/50 text-xs truncate">
              {selectedSite?.address || selectedSite?.municipality || (manualCode && !selectedSite ? 'Sin site en portfolio — solo se registra aquí' : '')}
            </div>
          </div>
          <button type="button" onClick={clear}
            data-testid={`${testIdPrefix}-btn-change-site`}
            className="text-xs text-[#A3FF12] hover:underline flex-shrink-0">Cambiar</button>
        </div>
      ) : (
        <>
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre, código, dirección o municipio…"
            data-testid={`${testIdPrefix}-site-search`}
            className="w-full bg-black/40 border border-white/10 focus:border-[#A3FF12]/50 rounded-lg px-3 py-2 mt-1 text-white text-sm outline-none"/>
          {search && (
            <>
              <div className="mt-1 max-h-48 overflow-y-auto border border-white/10 rounded-lg bg-black/30 divide-y divide-white/5">
                {filteredSites.length === 0 ? (
                  <div className="p-3 text-white/40 text-xs text-center">Ningún site coincide con la búsqueda.</div>
                ) : filteredSites.slice(0, 30).map((s) => (
                  <button type="button" key={s.id} onClick={() => pickSite(s)}
                    data-testid={`${testIdPrefix}-select-site-${s.id}`}
                    className="w-full text-left px-3 py-2 text-xs hover:bg-[#A3FF12]/10 hover:text-[#A3FF12] text-white/80">
                    <div className="font-bold flex items-center gap-2">
                      <span className="truncate">{s.name}</span>
                      {s.code && <span className="text-white/40 font-normal">· {s.code}</span>}
                    </div>
                    <div className="text-white/40 text-[10px] truncate mt-0.5">{s.address || s.municipality || '—'}</div>
                  </button>
                ))}
                {filteredSites.length > 30 && (
                  <div className="px-3 py-1.5 text-[10px] text-white/40 text-center">Mostrando 30 de {filteredSites.length}</div>
                )}
              </div>
              {!filteredSites.some((s) => (s.code || '').toLowerCase() === search.trim().toLowerCase() || (s.name || '').toLowerCase() === search.trim().toLowerCase()) && search.trim().length >= 2 && (
                <button type="button" onClick={useManual}
                  data-testid={`${testIdPrefix}-btn-use-manual-code`}
                  className="w-full mt-1 border border-dashed border-orange-500/40 hover:bg-orange-500/10 text-orange-300 rounded-lg px-3 py-2 text-xs flex items-center gap-2">
                  <Plus size={12}/> Usar <b className="text-orange-200">"{search.trim()}"</b> como código manual
                </button>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default SitePicker;
